// src/Screens/superadmin/Users/OwnerEquipmentListing.jsx

import React, { useState, useEffect, useContext } from 'react';
import axios from "axios";
import AppContext from "../../../../../context/AppContext";
import { Container, Row, Col, Button, Table, Badge, Dropdown, Modal } from 'react-bootstrap';
import { FaFilter, FaEllipsisV, FaEye, FaTrash, FaTractor, FaArrowLeft, FaTools, FaCheckCircle, FaBoxes } from 'react-icons/fa';
import { MdAttachMoney } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AppPagination from '../../../../../components/general/pagination/Pagination';
import '../../../../../assets/css/superadmin/usersmangment/rentalownermangment/ownerequipment/OwnerEquipment.css';
import MasterLayout from '../../../../../layout/superadmin/MasterLayout';

const OwnerEquipment = () => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { ownerId, owner } = location.state || {};

  // ── States ──
  const [equipmentData, setEquipmentData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [loading, setLoading] = useState(false);

  const itemsPerPage = 10;

  // ── Redirect if no owner ──
  useEffect(() => {
    if (!owner) navigate('/superadmin/users/rental-owners');
  }, [owner, navigate]);

  // ── Fetch Equipments ──
  const fetchEquipments = () => {
    console.log("ownerId =", ownerId);
    setLoading(true);
    const formData = new FormData();
    formData.append("owner_id", ownerId);

    axios.post(`${baseUrl}/superadmin/rentals/viewOwnerEquipments`, formData, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (res.data.success) {
          setEquipmentData(res.data.equipments || []);
          setFilteredData(res.data.equipments || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        Swal.fire({ icon: "error", title: "Error", text: "Unable to fetch equipments." });
      });
  };

  useEffect(() => {
    if (!ownerId) return;
    fetchEquipments();
  }, [ownerId]);

  // ── Stat Calculations — DB field is "status" ──
  const totalEquipment = equipmentData.length;
  const availableCount = equipmentData.filter(e => (e.status || '').toLowerCase() === 'available').length;
  const rentedCount = equipmentData.filter(e => (e.status || '').toLowerCase() === 'rented').length;
  const maintenanceCount = equipmentData.filter(e => (e.status || '').toLowerCase() === 'maintenance').length;

  // ── Category Filter ──
  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setFilteredData(category === 'All' ? equipmentData : equipmentData.filter(i => i.category_id == category));
    setCurrentPage(1);
  };

  // ── Status Filter ──
  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    if (status === 'All') {
      setFilteredData(equipmentData);
    } else {
      setFilteredData(equipmentData.filter(item => (item.status || '').toLowerCase() === status.toLowerCase()));
    }
    setCurrentPage(1);
  };

  // ── View Detail ──
  const handleViewDetail = (equipment) => {
    setSelectedEquipment(equipment);
    setShowDetailModal(true);
  };

  // ── Status Helpers ──
  const getStatusBadge = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'available': return { bg: 'success', text: 'Available' };
      case 'rented': return { bg: 'primary', text: 'Rented' };
      case 'maintenance': return { bg: 'warning', text: 'Maintenance' };
      default: return { bg: 'secondary', text: status || '—' };
    }
  };

  const getStatusDotClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'available': return 'available';
      case 'rented': return 'rented';
      case 'maintenance': return 'maintenance';
      default: return 'available';
    }
  };

  // ── Pagination ──
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEquipments = filteredData.slice(startIndex, startIndex + itemsPerPage);

  if (!owner) return null;

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <MasterLayout>
      <div className="equipment-listing-page">
        <Container fluid className="py-4">

          {/* HEADER */}
          <Row className="mb-4">
            <Col>
              <Button
                variant="outline-secondary"
                className="mb-3"
                onClick={() => navigate('/admin/users/rental-owners')}
                style={{ fontFamily: 'Poppins', fontWeight: 500, padding: '0.5rem 1.5rem', borderRadius: '8px', border: '1px solid #E5E5E5', color: '#000', backgroundColor: '#fff' }}
              >
                <FaArrowLeft className="me-2" /> Back to Rental Owners
              </Button>
              <h1 className="page-title">{owner.companyName || owner.srbusinessdetails?.businessname || owner.fullname} — All Equipment</h1>
              <p className="page-subtitle">
                Owner: {owner.fullname} | Total Equipment: {totalEquipment} Units
              </p>
            </Col>
          </Row>

          {/* STAT CARDS */}
          <Row className="mb-4">
            {[
              { icon: <FaBoxes />, value: totalEquipment, label: "Total Equipment" },
              { icon: <FaCheckCircle />, value: availableCount, label: "Available" },
              { icon: <MdAttachMoney />, value: rentedCount, label: "Currently Rented" },
              { icon: <FaTools />, value: maintenanceCount, label: "Under Maintenance" },
            ].map((s, i) => (
              <Col md={3} key={i}>
                <div className="oe-stat-card">
                  <span className="oe-stat-icon">{s.icon}</span>
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                </div>
              </Col>
            ))}
          </Row>

          {/* OWNER INFO & FILTERS */}
          <Row className="mb-4">
            <Col xl={12} lg={12} md={12} className="mb-3 mb-lg-0">
              <div className="owner-info-cards">
                <div className="owner-info-item">
                  <span className="info-label">Total Revenue</span>
                  <span className="info-value">Rs. {Number(owner.totalRevenue || 0).toLocaleString()}</span>
                </div>
                <div className="owner-info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{owner.email || 'N/A'}</span>
                </div>
                <div className="owner-info-item">
                  <span className="info-label">Phone Number</span>
                  <span className="info-value">{owner.phonenumber || 'N/A'}</span>
                </div>
                <div className="owner-info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{owner.address || 'N/A'}</span>
                </div>
              </div>
            </Col>
          </Row>

          {/* TABLE */}
          <Row>
            <Col>
              <div className="table-container">
                <Table hover className="equipment-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>EQUIPMENT NAME</th>
                      <th>SHOP NAME</th>
                      <th>RATE (RS)</th>
                      <th>LOCATION</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="spinner-border text-warning" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : paginatedEquipments.length > 0 ? (
                      paginatedEquipments.map((item, index) => (
                        <tr key={item.id}>
                          <td className="fw-semibold">{startIndex + index + 1}</td>

                          {/* Equipment Name + Image */}
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {item.image ? (
                                <img
                                  src={`${baseUrlForFile}/${item.image}`}
                                  alt={item.equipmentname}
                                  className="equipment-thumb"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div style={{ width: 38, height: 38, background: '#FFF8E6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <FaTractor size={16} color="#FDB714" />
                                </div>
                              )}
                              <span className="fw-medium">{item.equipmentname}</span>
                            </div>
                          </td>

                          {/* Shop Name — backend appends 'shop_name' (derived from srbusinessdetails) */}
                          <td>{item.shop_name || owner.companyName || owner.srbusinessdetails?.businessname || '—'}</td>

                          {/* Rate — equipment ke paas 3 alag rates hain (per hour / per day / per acre),
                              sirf jo basis par listing hui thi wahi field filled hoti hai, baqi NULL rehte hain.
                              Is liye jo bhi actually set hai wo dikhao, sirf rate_per_hour assume mat karo. */}
                          <td className="fw-semibold text-dark">
                            {item.rate_per_hour
                              ? `Rs. ${Number(item.rate_per_hour).toLocaleString()} /hr`
                              : item.rate_per_day
                              ? `Rs. ${Number(item.rate_per_day).toLocaleString()} /day`
                              : item.rate_per_acre
                              ? `Rs. ${Number(item.rate_per_acre).toLocaleString()} /acre`
                              : '—'}
                          </td>

                          {/* Location — equipment itself has no location field; backend appends 'shop_address' from srbusinessdetails */}
                          <td>{item.shop_address || '—'}</td>

                          {/* Status */}
                          <td>
                            <div className="status-text">
                              <span className={`status-dot ${getStatusDotClass(item.status)}`}></span>
                              {(item.status || '—').toUpperCase()}
                            </div>
                          </td>

                          {/* Actions */}
                          <td>
                            <Dropdown align="end">
                              <Dropdown.Toggle variant="link" className="action-toggle" bsPrefix="none">
                                <FaEllipsisV />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="action-menu">
                                <Dropdown.Item onClick={() => handleViewDetail(item)} className="action-item">
                                  <FaEye className="me-2" /> View Details
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <p className="text-muted mb-0">No equipment found for this owner</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <AppPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filteredData.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </Col>
          </Row>

        </Container>

        {/* ============================================
            DETAIL MODAL — Payout-Management style
        ============================================ */}
        <Modal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          centered
          size="lg"
          dialogClassName="equipment-detail-modal"
        >
          <Modal.Header closeButton className="equipment-modal-header">
            <Modal.Title>
              <FaTractor className="me-2" /> Equipment Details
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="equipment-modal-body">
            {selectedEquipment && (
              <Row>
                {/* LEFT — IMAGE */}
                <Col md={5} className="mb-3 mb-md-0">
                  <div className="equipment-image-card">
                    {selectedEquipment.image ? (
                      <img
                        src={`${baseUrlForFile}/${selectedEquipment.image}`}
                        alt={selectedEquipment.equipmentname}
                        className="equipment-detail-image"
                      />
                    ) : (
                      <div className="equipment-placeholder">
                        <FaTractor size={70} />
                      </div>
                    )}
                  </div>
                </Col>

                {/* RIGHT — INFO */}
                <Col md={7}>
                  <div className="info-card">
                    <h3 className="equipment-name">{selectedEquipment.equipmentname}</h3>
                    <p className="equipment-id">Equipment ID: #{selectedEquipment.id}</p>

                    <Badge
                      bg={getStatusBadge(selectedEquipment.status).bg}
                      className="status-badge-large"
                    >
                      {getStatusBadge(selectedEquipment.status).text}
                    </Badge>

                    <div className="section-title mt-3">Equipment Information</div>

                    <Row className="mb-2">
                      <Col xs={6}>
                        <label>Shop</label>
                        <p>{selectedEquipment.shop_name || owner.companyName || '—'}</p>
                      </Col>
                      <Col xs={6}>
                        <label>Location</label>
                        <p>{selectedEquipment.shop_address || '—'}</p>
                      </Col>
                    </Row>

                    <div className="detail-price">
                      {selectedEquipment.rate_per_hour
                        ? `Rs. ${Number(selectedEquipment.rate_per_hour).toLocaleString()}`
                        : selectedEquipment.rate_per_day
                        ? `Rs. ${Number(selectedEquipment.rate_per_day).toLocaleString()}`
                        : selectedEquipment.rate_per_acre
                        ? `Rs. ${Number(selectedEquipment.rate_per_acre).toLocaleString()}`
                        : 'Rs. 0'}
                      <span>
                        {selectedEquipment.rate_per_hour
                          ? 'Rate per Hour'
                          : selectedEquipment.rate_per_day
                          ? 'Rate per Day'
                          : selectedEquipment.rate_per_acre
                          ? 'Rate per Acre'
                          : 'Rate'}
                      </span>
                    </div>

                    <div className="section-title mt-4">About</div>
                    <p className="description-text">
                      {selectedEquipment.about || 'No description provided.'}
                    </p>
                  </div>
                </Col>
              </Row>
            )}
          </Modal.Body>

          <Modal.Footer className="equipment-modal-footer">
            <Button className="close-btn" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

      </div>
    </MasterLayout>
  );
};

export default OwnerEquipment;